import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NeuralBackground } from './NeuralBackground'

const SOCIAL = {
  linkedin: 'https://www.linkedin.com/in/daisuke-akagawa-ab0759161',
  github: 'https://github.com/Akasan',
}

export function Hero() {
  const { t } = useTranslation('home')

  const stats = [
    t('hero.stats.experience'),
    t('hero.stats.certs'),
    t('hero.stats.role'),
  ]

  return (
    <section className="relative overflow-hidden bg-slate-950 py-20 lg:py-28">
      {/* Animated neural-network background */}
      <NeuralBackground />
      {/* Depth glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl"
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: self-introduction */}
          <div className="order-2 lg:order-1">
            <p className="inline-flex items-center gap-2 text-sm font-medium text-cyan-300 bg-cyan-400/10 ring-1 ring-inset ring-cyan-400/20 px-3 py-1 rounded-full mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" aria-hidden />
              {t('hero.greeting')}
            </p>
            <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-white mb-3">
              {t('hero.name')}
            </h1>
            <p className="text-lg lg:text-xl font-semibold text-cyan-400 mb-5">
              {t('hero.role')}
            </p>
            <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-7 max-w-xl">
              {t('hero.bio')}
            </p>

            {/* Quick facts */}
            <div className="flex flex-wrap gap-2 mb-8">
              {stats.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-200 bg-white/5 ring-1 ring-inset ring-white/10 px-3 py-1.5 rounded-full backdrop-blur-sm"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" aria-hidden />
                  {s}
                </span>
              ))}
            </div>

            {/* CTAs + social */}
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 bg-cyan-500 text-slate-950 px-6 py-3 rounded-lg font-semibold hover:bg-cyan-400 transition-colors"
              >
                {t('hero.viewProfile')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-lg font-medium ring-1 ring-inset ring-white/20 hover:bg-white/10 transition-colors"
              >
                {t('hero.readBlog')}
              </Link>
              <div className="flex items-center gap-2 sm:ml-1">
                <a
                  href={SOCIAL.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="w-11 h-11 text-slate-200 ring-1 ring-inset ring-white/15 rounded-full flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href={SOCIAL.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  className="w-11 h-11 text-slate-200 ring-1 ring-inset ring-white/15 rounded-full flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Right: portrait */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-cyan-500/30 to-indigo-500/30 rounded-full blur-2xl" aria-hidden />
              <div className="relative w-64 h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden ring-2 ring-white/15 shadow-2xl shadow-cyan-500/10">
                <picture>
                  <source srcSet="/profile_image.webp" type="image/webp" />
                  <img
                    src="/profile_image_640.jpg"
                    alt={t('hero.name')}
                    width={320}
                    height={320}
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 lg:left-2 lg:translate-x-0 bg-white/10 ring-1 ring-inset ring-white/15 backdrop-blur-md rounded-full px-4 py-2 shadow-lg flex items-center gap-2 whitespace-nowrap">
                <span className="relative flex w-2 h-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full w-2 h-2 bg-green-500"></span>
                </span>
                <span className="text-sm text-slate-100">{t('hero.statusActive')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
